import type { ReduxAction } from "actions/ReduxActionTypes";
import {
  ReduxActionTypes,
  ReduxActionErrorTypes,
} from "ee/constants/ReduxActionConstants";
import { createImmerReducer } from "utils/ReducerUtils";
import type { SSHKey, SSHKeyDTO, SSHKeyApiError } from "ee/types/sshKeysTypes";

// --- Types ---

export interface SSHKeyAsyncState<T = unknown> {
  value: T | null;
  loading: boolean;
  error: SSHKeyApiError | null;
}

export interface SSHKeyAsyncStateWithoutValue {
  loading: boolean;
  error: SSHKeyApiError | null;
}

export interface SSHKeysReduxState {
  keys: SSHKeyAsyncState<SSHKey[]>;
  createSSHKey: SSHKeyAsyncStateWithoutValue;
  deleteSSHKey: SSHKeyAsyncStateWithoutValue;
  regenerateSSHKey: SSHKeyAsyncStateWithoutValue;
  shareSSHKey: SSHKeyAsyncStateWithoutValue;
  revokeSSHKey: SSHKeyAsyncStateWithoutValue;
  exportSSHKey: SSHKeyAsyncState<SSHKeyDTO>;
  importSSHKey: SSHKeyAsyncStateWithoutValue;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isShareModalOpen: boolean;
  isImportModalOpen: boolean;
  selectedKeyId: string | null;
  exportingKeyId: string | null;
}

export interface SSHKeysRootState {
  sshKeys: SSHKeysReduxState;
}

// --- Initial state ---

const initialState: SSHKeysReduxState = {
  keys: { value: null, loading: false, error: null },
  createSSHKey: { loading: false, error: null },
  deleteSSHKey: { loading: false, error: null },
  regenerateSSHKey: { loading: false, error: null },
  shareSSHKey: { loading: false, error: null },
  revokeSSHKey: { loading: false, error: null },
  exportSSHKey: { value: null, loading: false, error: null },
  importSSHKey: { loading: false, error: null },
  isCreateModalOpen: false,
  isDeleteModalOpen: false,
  isShareModalOpen: false,
  isImportModalOpen: false,
  selectedKeyId: null,
  exportingKeyId: null,
};

// --- Handlers ---

const handlers = {
  // Fetch
  [ReduxActionTypes.FETCH_SSH_KEYS_INIT]: (state: SSHKeysReduxState) => {
    state.keys.loading = true;
    state.keys.error = null;
  },
  [ReduxActionTypes.FETCH_SSH_KEYS_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<SSHKey[]>,
  ) => {
    state.keys.loading = false;
    state.keys.value = action.payload;
    state.keys.error = null;
  },
  [ReduxActionErrorTypes.FETCH_SSH_KEYS_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.keys.loading = false;
    state.keys.error = action.payload.error;
  },

  // Create
  [ReduxActionTypes.CREATE_SSH_KEY_INIT]: (state: SSHKeysReduxState) => {
    state.createSSHKey.loading = true;
    state.createSSHKey.error = null;
  },
  [ReduxActionTypes.CREATE_SSH_KEY_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<SSHKey>,
  ) => {
    state.createSSHKey.loading = false;
    state.createSSHKey.error = null;

    if (state.keys.value) {
      state.keys.value = [action.payload, ...state.keys.value];
    } else {
      state.keys.value = [action.payload];
    }

    state.isCreateModalOpen = false;
  },
  [ReduxActionErrorTypes.CREATE_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.createSSHKey.loading = false;
    state.createSSHKey.error = action.payload.error;
  },

  // Delete
  [ReduxActionTypes.DELETE_SSH_KEY_INIT]: (state: SSHKeysReduxState) => {
    state.deleteSSHKey.loading = true;
    state.deleteSSHKey.error = null;
  },
  [ReduxActionTypes.DELETE_SSH_KEY_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ id: string }>,
  ) => {
    state.deleteSSHKey.loading = false;
    state.deleteSSHKey.error = null;

    if (state.keys.value) {
      state.keys.value = state.keys.value.filter(
        (key) => key.id !== action.payload.id,
      );
    }

    state.isDeleteModalOpen = false;
    state.selectedKeyId = null;
  },
  [ReduxActionErrorTypes.DELETE_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.deleteSSHKey.loading = false;
    state.deleteSSHKey.error = action.payload.error;
  },

  // Import
  [ReduxActionTypes.IMPORT_SSH_KEY_INIT]: (state: SSHKeysReduxState) => {
    state.importSSHKey.loading = true;
    state.importSSHKey.error = null;
  },
  [ReduxActionTypes.IMPORT_SSH_KEY_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<SSHKey>,
  ) => {
    state.importSSHKey.loading = false;
    state.importSSHKey.error = null;

    if (state.keys.value) {
      state.keys.value = [action.payload, ...state.keys.value];
    } else {
      state.keys.value = [action.payload];
    }

    state.isImportModalOpen = false;
  },
  [ReduxActionErrorTypes.IMPORT_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.importSSHKey.loading = false;
    state.importSSHKey.error = action.payload.error;
  },

  // Export
  [ReduxActionTypes.EXPORT_SSH_KEY_INIT]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ id: string }>,
  ) => {
    state.exportSSHKey.loading = true;
    state.exportSSHKey.error = null;
    state.exportingKeyId = action.payload.id;
  },
  [ReduxActionTypes.EXPORT_SSH_KEY_SUCCESS]: (state: SSHKeysReduxState) => {
    state.exportSSHKey.loading = false;
    state.exportSSHKey.value = null;
    state.exportSSHKey.error = null;
    state.exportingKeyId = null;
  },
  [ReduxActionErrorTypes.EXPORT_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.exportSSHKey.loading = false;
    state.exportSSHKey.error = action.payload.error;
    state.exportingKeyId = null;
  },
  [ReduxActionTypes.CLEAR_EXPORT_SSH_KEY]: (state: SSHKeysReduxState) => {
    state.exportSSHKey.value = null;
    state.exportSSHKey.error = null;
  },

  // Share
  [ReduxActionTypes.SHARE_SSH_KEY_INIT]: (state: SSHKeysReduxState) => {
    state.shareSSHKey.loading = true;
    state.shareSSHKey.error = null;
  },
  [ReduxActionTypes.SHARE_SSH_KEY_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<SSHKey>,
  ) => {
    state.shareSSHKey.loading = false;
    state.shareSSHKey.error = null;

    if (state.keys.value) {
      state.keys.value = state.keys.value.map((key) =>
        key.id === action.payload.id ? action.payload : key,
      );
    }

    state.isShareModalOpen = false;
    state.selectedKeyId = null;
  },
  [ReduxActionErrorTypes.SHARE_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.shareSSHKey.loading = false;
    state.shareSSHKey.error = action.payload.error;
  },

  // Revoke
  [ReduxActionTypes.REVOKE_SSH_KEY_INIT]: (state: SSHKeysReduxState) => {
    state.revokeSSHKey.loading = true;
    state.revokeSSHKey.error = null;
  },
  [ReduxActionTypes.REVOKE_SSH_KEY_SUCCESS]: (
    state: SSHKeysReduxState,
    action: ReduxAction<SSHKey>,
  ) => {
    state.revokeSSHKey.loading = false;
    state.revokeSSHKey.error = null;

    if (state.keys.value) {
      state.keys.value = state.keys.value.map((key) =>
        key.id === action.payload.id ? action.payload : key,
      );
    }
  },
  [ReduxActionErrorTypes.REVOKE_SSH_KEY_ERROR]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ error: SSHKeyApiError }>,
  ) => {
    state.revokeSSHKey.loading = false;
    state.revokeSSHKey.error = action.payload.error;
  },

  // UI - Modals
  [ReduxActionTypes.OPEN_SSH_KEY_CREATE_MODAL]: (state: SSHKeysReduxState) => {
    state.isCreateModalOpen = true;
  },
  [ReduxActionTypes.CLOSE_SSH_KEY_CREATE_MODAL]: (state: SSHKeysReduxState) => {
    state.isCreateModalOpen = false;
    state.createSSHKey.error = null;
  },
  [ReduxActionTypes.OPEN_SSH_KEY_DELETE_MODAL]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ id: string }>,
  ) => {
    state.isDeleteModalOpen = true;
    state.selectedKeyId = action.payload.id;
  },
  [ReduxActionTypes.CLOSE_SSH_KEY_DELETE_MODAL]: (state: SSHKeysReduxState) => {
    state.isDeleteModalOpen = false;
    state.selectedKeyId = null;
    state.deleteSSHKey.error = null;
  },
  [ReduxActionTypes.OPEN_SSH_KEY_SHARE_MODAL]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ id: string }>,
  ) => {
    state.isShareModalOpen = true;
    state.selectedKeyId = action.payload.id;
  },
  [ReduxActionTypes.CLOSE_SSH_KEY_SHARE_MODAL]: (state: SSHKeysReduxState) => {
    state.isShareModalOpen = false;
    state.selectedKeyId = null;
    state.shareSSHKey.error = null;
  },
  [ReduxActionTypes.OPEN_SSH_KEY_IMPORT_MODAL]: (state: SSHKeysReduxState) => {
    state.isImportModalOpen = true;
  },
  [ReduxActionTypes.CLOSE_SSH_KEY_IMPORT_MODAL]: (state: SSHKeysReduxState) => {
    state.isImportModalOpen = false;
    state.importSSHKey.error = null;
  },
  [ReduxActionTypes.SET_SELECTED_SSH_KEY_ID]: (
    state: SSHKeysReduxState,
    action: ReduxAction<{ id: string | null }>,
  ) => {
    state.selectedKeyId = action.payload.id;
  },
  [ReduxActionTypes.RESET_SSH_KEYS_STATE]: () => initialState,
};

export default createImmerReducer(initialState, handlers);
